import { DatePicker, Form, ConfigProvider, Select, Space, Button, Divider, Table, Modal, Descriptions } from 'antd'
import { SearchOutlined, UndoOutlined, ApiOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import vi_VN from 'antd/es/locale/vi_VN'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import 'dayjs/locale/vi'
import { useCallback, useEffect, useState } from 'react'
import { useLoading } from '../../context/LoadingContext'
import { apiGetListCompanies, apiGetListUsers, apiGetStatisticalTelesaleCalling } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { formatNumber } from '../../utils/string-utils'
import { IUser } from '../../models/user.model'
import { typeNoteCustomerOptions } from '../../assets/constants/default_options'
import ChartDayTelesaleCalling from '../../components/chart/ChartDayTelesaleCalling'
import ChartTypeY from '../../components/chart/ChartTypeY'
import ChartDay from '../../components/chart/ChartDay'
import { ICompany } from '../../models/company.modal'
import { breakdownColumns } from '../../helper/columns_helper'
import { ColumnsType } from 'antd/es/table'

const { RangePicker } = DatePicker

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.locale('vi')

export default function TelesaleUser() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [userOptions, setUserOptions] = useState<{ label: string, value: Number }[]>([])
    const [totalRecord, setTotalRecord] = useState<any>([])
    const [modalDays, setModalDays] = useState<any>({ isOpen: false, data: null })
    const [totalOwnerBy, setTotalOwnerBy] = useState<any>([])
    const [totalDay, setTotalDay] = useState<any>([])
    const [search, setSearch] = useState<any>({})
    const [defaultSearch, setDefaultSearch] = useState<any>()
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
    const [formSearch] = Form.useForm()
    const { setIsLoading } = useLoading()

    const ownerByColumns: ColumnsType<any> = [
        {
            key: 'owner_by',
            title: 'Nhân sự',
            dataIndex: 'owner_by',
        },
        ...breakdownColumns,
        {
            key: 'SUCCESS',
            title: 'Gia hạn',
            dataIndex: 'breakdown',
            render: breakdown => breakdown?.SUCCESS,
        },
        {
            key: 'total_calls',
            title: 'Tổng cộng',
            dataIndex: 'total_calls',
        },
    ]

    const monthColumns: ColumnsType<any> = [
        {
            key: 'month',
            title: 'Thời gian',
            render: (_: any, record: any) => `${record.year}-${record.month}`,
        },
        ...breakdownColumns,
        {
            key: 'SUCCESS',
            title: 'Gia hạn',
            dataIndex: 'breakdown',
            render: breakdown => breakdown?.SUCCESS,
        },
        {
            key: 'total_calls',
            title: 'Tổng cộng',
            dataIndex: 'total_calls',
        },
        {
            key: 'days',
            dataIndex: 'days',
            render: (days: any) => (
                <Button
                    size='small'
                    onClick={() => setModalDays({ isOpen: true, data: days })}
                    icon={<EyeOutlined />}
                />
            ),
        },
    ]

    const fetchOptions = useCallback(async () => {
        try {
            const [respUser, respCompany] = await Promise.all([
                apiGetListUsers({
                    limit: defaultParams.MAX_LIMIT,
                }),
                apiGetListCompanies({
                    limit: defaultParams.MAX_LIMIT,
                }),
            ])
            if (respUser.data?.users) {
                const userOptions = respUser.data.users
                    .map((item: IUser) => ({
                        label: item.username,
                        value: item.username,
                    }))
                setUserOptions(userOptions)
            }
            if (respCompany.data?.companies) {
                const compOptions = respCompany.data.companies
                    .map((item: ICompany) => ({
                        label: item.name,
                        value: item.code,
                    }))
                setCompanyOptions(compOptions)
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    function summarizeByOwner(data: any) {
        const summary: any = {};
        let total = 0;
        for (const item of data) {
            const { owner_by, amount } = item;
            summary[owner_by] = (summary[owner_by] || 0) + amount;
            total += amount;
        }
        const ownerBy = Object.entries(summary).map(([type, sum]) => ({ type, sum }));
        return { ownerBy, total };
    }

    function summarizeByDay(data: any) {
        const summary: any = {};
        for (const item of data) {
            const { month, day, amount } = item;
            if (!summary[month]) {
                summary[month] = {
                    sum: 0,
                    days: Array.from({ length: 31 }, (_, i) => ({ day: i + 1, sum: 0 }))
                };
            }
            summary[month].sum += amount;
            if (day >= 1 && day <= 31) {
                summary[month].days[day - 1].sum += amount;
            }
        }
        const months = Object.entries(summary).map(([month, { sum, days }]: any) => ({
            month: parseInt(month),
            sum,
            days
        }));
        months.sort((a, b) => a.month - b.month);
        return months;
    }

    function aggregateHierarchicalTelesaleCalling(data: any) {
        const result: any = {};
        let total = 0;
        let success = 0;
        let fail = 0;
        let breakdown: any = {};
        for (const item of data) {
            const { owner_by, year, month, day, amount_type, amount } = item;
            total += amount;
            if (amount_type === 'SUCCESS') {
                success += amount;
            } else {
                fail += amount;
                breakdown['FAIL'] = (breakdown['FAIL'] || 0) + amount;
            }
            breakdown[amount_type] = (breakdown[amount_type] || 0) + amount;
            if (!result[owner_by]) {
                result[owner_by] = {
                    owner_by,
                    total_calls: 0,
                    breakdown: {},
                    months: []
                };
            }
            const owner = result[owner_by];
            if (amount_type !== 'SUCCESS') {
                owner.breakdown['FAIL'] = (owner.breakdown['FAIL'] || 0) + amount;
            }
            owner.breakdown[amount_type] = (owner.breakdown[amount_type] || 0) + amount;
            owner.total_calls += amount;
            let monthObj = owner.months.find((m: any) => m.year === year && m.month === month);
            if (!monthObj) {
                monthObj = {
                    month,
                    year,
                    total_calls: 0,
                    breakdown: {},
                    days: []
                };
                owner.months.push(monthObj);
            }
            if (amount_type !== 'SUCCESS') {
                monthObj.breakdown['FAIL'] = (monthObj.breakdown['FAIL'] || 0) + amount;
            }
            monthObj.breakdown[amount_type] = (monthObj.breakdown[amount_type] || 0) + amount;
            monthObj.total_calls += amount;
            let dayObj = monthObj.days.find((d: any) => d.day === day);
            if (!dayObj) {
                dayObj = {
                    day,
                    total_calls: 0,
                    breakdown: {}
                };
                monthObj.days.push(dayObj);
            }
            if (amount_type !== 'SUCCESS') {
                dayObj.breakdown['FAIL'] = (dayObj.breakdown['FAIL'] || 0) + amount;
            }
            dayObj.breakdown[amount_type] = (dayObj.breakdown[amount_type] || 0) + amount;
            dayObj.total_calls += amount;
        }
        const record: any = Object.values(result);
        for (const user of record) {
            user.months.sort((a: any, b: any) =>
                a.year === b.year ? a.month - b.month : a.year - b.year
            );
            for (const month of user.months) {
                month.days.sort((a: any, b: any) => a.day - b.day);
            }
        }
        return { record, total, success, fail, breakdown };
    }

    const fetchStatistics = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetStatisticalTelesaleCalling(search)
            if (data?.statistics != null) {
                setTotalOwnerBy(summarizeByOwner(data.statistics))
                setTotalDay(summarizeByDay(data.statistics))
                setTotalRecord(aggregateHierarchicalTelesaleCalling(data.statistics))
            } else {
                setTotalOwnerBy({})
                setTotalDay([])
                setTotalRecord([])
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search])

    useEffect(() => {
        const now = new Date()
        const year = now.getFullYear()
        const defaultParams = {
            startDate: `${year}-01-01`,
            endDate: `${year}-12-31`,
        }
        setDefaultSearch(defaultParams)
        setSearch(defaultParams)
    }, [])

    useEffect(() => {
        fetchOptions()
        if (defaultSearch) {
            fetchStatistics()
        }
    }, [fetchOptions, fetchStatistics, defaultSearch])

    const handleSearch = (values: any) => {
        const rangeDate = values?.dateExpired
        if (rangeDate) {
            const startDate = rangeDate[0] ? rangeDate[0]?.format('YYYY-MM-DD') : null
            const endDate = rangeDate[1] ? rangeDate[1]?.format('YYYY-MM-DD') : null
            values.startDate = startDate
            values.endDate = endDate
        }

        setSearch({
            cpCode: values.cpCode,
            ownerBy: values.ownerBy,
            amountType: values.amountType,
            startDate: values.startDate || defaultSearch.startDate,
            endDate: values.endDate || defaultSearch.endDate,
        })
    }

    const handleClearSearch = () => {
        setSearch(defaultSearch)
        formSearch.resetFields()
    }

    return (
        <div>
            <Form
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    alignItems: 'center',
                    marginBottom: 20,
                }}
                form={formSearch} onFinish={handleSearch} id="searchForm" layout="vertical" autoComplete="off">
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    flexWrap: 'wrap',
                    gap: 20,
                    width: '100%',
                }}>
                    <Form.Item name="cpCode" label={<b>Đối tác</b>}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn đối tác"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                ...companyOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="ownerBy" label={<b>Nhân sự</b>}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn nhân sự"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                ...userOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="amountType" label="Loại">
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn loại"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                { label: 'Gia hạn', value: 'SUCCESS' },
                                ...typeNoteCustomerOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    {defaultSearch && (
                        <ConfigProvider locale={vi_VN}>
                            <Form.Item name="dateExpired" label="Khoảng thời gian">
                                <RangePicker
                                    defaultValue={[dayjs(defaultSearch.startDate), dayjs(defaultSearch.endDate)]}
                                    format="YYYY-MM-DD"
                                    picker="date" />
                            </Form.Item>
                        </ConfigProvider>
                    )}
                    <Space>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 20 }}>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <ApiOutlined /> Tổng cộng: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.total || 0)}</span>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <CheckOutlined style={{ color: 'green' }} /> Gia hạn: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.success || 0)}</span>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <CloseOutlined style={{ color: 'red' }} /> Từ chối: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.fail || 0)}</span>
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
                {!search.ownerBy && totalOwnerBy?.total > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartTypeY
                            data={totalOwnerBy.ownerBy}
                            title='Tổng số lượng theo nhân sự'
                        />
                    </div>
                )}
                {totalDay?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartDay
                            data={totalDay}
                            title='Tổng số lượng theo ngày'
                        />
                    </div>
                )}
            </div>
            <Divider />
            <h2>Chi tiết theo nhân sự</h2>
            <Table
                rowKey="owner_by"
                columns={ownerByColumns}
                dataSource={totalRecord.record}
                expandable={{
                    expandedRowRender: (record: any) => (
                        <div style={{ marginBottom: 40, padding: 10 }}>
                            <Table
                                size='small'
                                rowKey={(_, index: any) => index}
                                style={{ overflow: 'auto' }}
                                columns={monthColumns}
                                dataSource={record.months}
                                pagination={false}
                            />
                        </div>
                    ),
                    expandRowByClick: true,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (expanded, record) => {
                        if (expanded) {
                            setExpandedRowKeys([record.owner_by]);
                        } else {
                            setExpandedRowKeys([]);
                        }
                    }
                }}
                footer={() => (
                    <Descriptions title="Tổng kết" items={[
                        {
                            label: 'Tổng đại lý',
                            children: formatNumber(totalRecord.breakdown?.AGENCY || 0),
                        },
                        {
                            label: 'Tổng không phải chủ xe',
                            children: formatNumber(totalRecord.breakdown?.NOT_OWNER || 0),
                        },
                        {
                            label: 'Tổng khách gọi lại sau',
                            children: formatNumber(totalRecord.breakdown?.CALL_LATER || 0),
                        },
                        {
                            label: 'Tổng không nghe máy',
                            children: formatNumber(totalRecord.breakdown?.NO_ANSWER || 0),
                        },
                        {
                            label: 'Tổng không có nhu cầu',
                            children: formatNumber(totalRecord.breakdown?.NO_NEED || 0),
                        },
                        {
                            label: 'Tổng đã mua SIM mới',
                            children: formatNumber(totalRecord.breakdown?.BOUGHT_NEW || 0),
                        },
                        {
                            label: 'Tổng khác',
                            children: formatNumber(totalRecord.breakdown?.OTHER_REASONS || 0),
                        },
                        {
                            label: 'Tổng thất bại',
                            children: formatNumber(totalRecord.fail || 0),
                        },
                        {
                            label: 'Tổng gia hạn',
                            children: formatNumber(totalRecord.success || 0),
                        },
                        {
                            label: 'Tổng số lượng',
                            children: formatNumber(totalRecord.total || 0),
                        },
                        {
                            label: 'Tổng nhân sự',
                            children: formatNumber(totalOwnerBy?.ownerBy?.length || 0),
                        },
                    ]} />
                )}
            />
            <Modal width={'60%'} title='Chi tiết theo ngày' open={modalDays.isOpen}
                onCancel={() => setModalDays({ isOpen: false, data: null })}
                footer={[]}>
                <Divider />
                <ChartDayTelesaleCalling data={modalDays.data} />
                <Divider />
            </Modal>
        </div>
    )
}
